import React, {
  FormHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useFormik } from 'formik'
import gsap from 'gsap'
import parse from 'html-react-parser'

import Button from '@/components/base/button'
import CtaButton from '@/components/base/cta-button'
import FormControl from '@/components/base/form-control'
import Typography from '@/components/base/typography'
import { useLenis } from '@/contexts/lenis'
import { useLocaleStore } from '@/store/locale'
import {
  convertJsontoFormData,
  createInitialValues,
  createYupSchema
} from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { cn } from '@/lib/utils'
import { WPGravityForm } from '@/types'

export type FormVariant = 'default' | 'newsletter' | 'outlined' | 'light'

export type FormProps = FormHTMLAttributes<HTMLFormElement> &
  Partial<{
    form_id: number
    submit: string
    fields: WPGravityForm['fields']
    cta_button: boolean
    variant: FormVariant
    fieldOverrides: { [adminLabel: string]: string }
    // eslint-disable-next-line no-unused-vars
    onSuccess: (values: any) => void
    onCloseFeedback: () => void
    selectDependencies: { [adminLabel: string]: string[] }
    // eslint-disable-next-line no-unused-vars
    onFieldChange: (adminLabel: string, value: string) => void
  }>

const Form = ({
  form_id,
  fields = [],
  submit,
  cta_button = false,
  variant = 'default',
  className,
  onSuccess,
  onCloseFeedback,
  fieldOverrides,
  selectDependencies,
  onFieldChange,
  ...props
}: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('')
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [portalMounted, setPortalMounted] = useState(false)
  const [selectResetKeys, setSelectResetKeys] = useState<{
    [key: string]: number
  }>({})
  const formRef = useRef<HTMLFormElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const okButtonRef = useRef<HTMLButtonElement>(null)
  const currentLocale = useLocaleStore((s) => s.currentLocale) || 'it'
  const { scrollTo } = useLenis()
  const fieldByAdminLabel = useMemo(
    () =>
      fields.reduce(
        (acc, field) => {
          if (field.adminLabel) {
            acc[field.adminLabel] = field
          }
          return acc
        },
        {} as Record<string, WPGravityForm['fields'][number]>
      ),
    [fields]
  )

  useEffect(() => {
    setPortalMounted(true)
  }, [])

  useEffect(() => {
    if (!modalRef.current) return
    gsap.to(modalRef.current, {
      opacity: feedbackOpen ? 1 : 0,
      pointerEvents: feedbackOpen ? 'auto' : 'none',
      duration: 0.3,
      ease: 'power2.out'
    })
    if (feedbackOpen && okButtonRef.current) {
      okButtonRef.current.focus()
    }
  }, [feedbackOpen])

  useEffect(() => {
    if (!feedbackOpen) return
    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [feedbackOpen])

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const resolvedOverrides = fieldOverrides
    ? fields.reduce(
        (acc, field) => {
          if (
            field.adminLabel &&
            fieldOverrides[field.adminLabel] !== undefined
          ) {
            acc[`input_${field.id}`] = fieldOverrides[field.adminLabel]
          }
          return acc
        },
        {} as Record<string, string>
      )
    : {}

  const formik = useFormik<any>({
    initialValues: {
      ...(createInitialValues(fields) || {}),
      ...resolvedOverrides
    },
    validationSchema: createYupSchema(fields, currentLocale),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const result = sendContactForm({
        ...values
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
          if (response.data.is_valid && onSuccess) {
            onSuccess(values)
          }
          setFeedbackOpen(true)
        })
        .then(() => {
          // Genera nuove chiavi di reset per le select
          const newResetKeys = fields.reduce(
            (acc: { [key: string]: number }, field) => {
              if (field.type === 'select') {
                acc[`input_${field.id}`] =
                  (selectResetKeys[`input_${field.id}`] || 0) + 1
              }
              return acc
            },
            {}
          )

          setSelectResetKeys(newResetKeys)
          resetForm()
          setSubmitting(false)
        })
    }
  })

  const handleSubmitWithScroll = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault()
    const errors = await formik.validateForm()
    const touchedFields = Object.keys(formik.values).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    )
    formik.setTouched(touchedFields)

    if (Object.keys(errors).length > 0) {
      const firstErrorField = fields.find(
        (field) => errors[`input_${field.id}`]
      )
      const firstErrorKey = firstErrorField
        ? `input_${firstErrorField.id}`
        : Object.keys(errors)[0]
      const firstErrorElement = formRef.current?.querySelector(
        `[name="${firstErrorKey}"]`
      )
      if (firstErrorElement) {
        scrollTo(firstErrorElement as HTMLElement, {
          force: true,
          duration: 1,
          offset: -100
        })
      }
      return
    }

    formik.submitForm()
  }

  return (
    <form
      ref={formRef}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmitWithScroll}
      className={cn(['grid grid-cols-2 gap-x-[10rem] gap-y-[8rem]', className])}
      {...props}
    >
      {fields?.map((field, index) => {
        return (
          <FormControl
            key={index}
            variant={variant}
            resetKey={selectResetKeys[`input_${field.id}`]?.toString()}
            id={`input_${field.id}`}
            name={`input_${field.id}`}
            adminLabel={field.adminLabel}
            visibility={
              field.visibility as 'hidden' | 'visible' | 'collapsed' | undefined
            }
            type={field.type}
            className={cn([
              'col-span-2',
              {
                'col-span-2 md:col-span-1': field.layoutGridColumnSpan === 6
                // 'lg:col-span-7': field.type === 'consent'
              }
            ])}
            label={field.type === 'consent' ? field.checkboxLabel : field.label}
            required={field.isRequired}
            choices={field.choices}
            placeholder={
              variant === 'newsletter' || variant === 'light'
                ? field.label
                : field.placeholder
            }
            checked={formik.values[`input_${field.id}`] || false}
            value={formik.values[`input_${field.id}`]}
            disabled={formik.isSubmitting}
            onChange={(e: any) => {
              if (field.type === 'consent') {
                formik.handleChange(e)
                formik.setFieldValue(
                  `input_${field.id}_1`,
                  e.target.checked ? '1' : '0',
                  false
                )
              } else if (field.type === 'select') {
                const nextValue = typeof e === 'string' ? e : ''
                formik.setFieldValue(`input_${field.id}`, nextValue, false)

                if (field.adminLabel) {
                  const visited = new Set<string>()
                  const clearDependentFields = (adminLabel: string) => {
                    const dependencies = selectDependencies?.[adminLabel] || []

                    dependencies.forEach((dependentAdminLabel) => {
                      if (visited.has(dependentAdminLabel)) return
                      visited.add(dependentAdminLabel)

                      const dependentField =
                        fieldByAdminLabel[dependentAdminLabel]
                      if (dependentField) {
                        formik.setFieldValue(
                          `input_${dependentField.id}`,
                          '',
                          false
                        )

                        // Force remount of dependent selects so placeholder is restored.
                        if (dependentField.type === 'select') {
                          setSelectResetKeys((prev) => ({
                            ...prev,
                            [`input_${dependentField.id}`]:
                              (prev[`input_${dependentField.id}`] || 0) + 1
                          }))
                        }

                        onFieldChange?.(dependentAdminLabel, '')
                      }

                      clearDependentFields(dependentAdminLabel)
                    })
                  }

                  clearDependentFields(field.adminLabel)
                  onFieldChange?.(field.adminLabel, nextValue)
                }
              } else if (field.type === 'radio') {
                formik.setFieldValue(`input_${field.id}`, e, true)
              } else if (
                field.type === 'checkbox' ||
                field.type === 'multi_choice'
              ) {
                const selected: string[] = Array.isArray(e) ? e : []
                formik.setFieldValue(`input_${field.id}`, selected, true)
                const inputs = Array.isArray(field.inputs) ? field.inputs : []
                inputs.forEach((input) => {
                  const matchedChoice = field.choices?.find(
                    (c) => c.key === input.key
                  )
                  const key = `input_${String(input.id).replace('.', '_')}`
                  if (matchedChoice && selected.includes(matchedChoice.value)) {
                    formik.setFieldValue(key, matchedChoice.value, false)
                  } else {
                    formik.setFieldValue(key, '', false)
                  }
                })
              } else {
                formik.handleChange(e)
              }
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors[`input_${field.id}`] !== undefined &&
              formik.touched[`input_${field.id}`] !== undefined
            }
            errorMessage={
              formik.touched[`input_${field.id}`]
                ? (formik.errors[`input_${field.id}`] as string)
                : undefined
            }
          />
        )
      })}

      <div className="col-span-2 flex items-center" data-form-submit>
        {cta_button ? (
          <CtaButton
            variant="primary"
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
          />
        ) : (
          <CtaButton
            variant="primary"
            className="w-full"
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
          >
            {submit}
          </CtaButton>
        )}
      </div>

      {portalMounted
        ? createPortal(
            <div
              ref={modalRef}
              className="pointer-events-none fixed inset-0 z-[110] flex h-[calc(100*var(--lvh))] items-center justify-center bg-[black]/65 opacity-0"
              role="dialog"
              aria-modal="true"
              aria-hidden={!feedbackOpen}
              data-lenis-prevent={true}
            >
              <div className="mx-spacing-md grid w-full max-w-[50rem] bg-text-primary-light p-spacing-lg text-white-light">
                <div className="text-center">
                  <Typography
                    variant="regular"
                    className={cn([
                      {
                        'text-white-light': formik.status !== 'error',
                        'text-red-500': formik.status === 'error'
                      }
                    ])}
                  >
                    {parse(statusMessage)}
                  </Typography>
                </div>
                <Button
                  ref={okButtonRef}
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setFeedbackOpen(false)
                    setStatusMessage('')
                    if (onCloseFeedback) {
                      onCloseFeedback()
                    }
                  }}
                  className="mt-spacing-sm"
                >
                  OK
                </Button>
              </div>
            </div>,
            document.body
          )
        : null}
    </form>
  )
}

export default Form
